import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/adminApi';
import {
    Users, UserCheck, Shield, TrendingUp, Search,
    MoreVertical, Trash2, ShieldCheck, ShieldOff,
    UserX, UserCheck as UserCheckIcon, Map, Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import './AdminDashboard.css';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        loadDashboardData();
    }, [currentPage, searchTerm]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const [statsData, usersData] = await Promise.all([
                adminApi.getStats(),
                adminApi.getUsers({ page: currentPage, limit: 10, search: searchTerm })
            ]);
            setStats(statsData.data);
            setUsers(usersData.data.users);
            setTotalPages(usersData.data.totalPages);
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAdmin = async (userId, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'remove' : 'grant'} admin privileges?`)) return;
        try {
            await adminApi.updateUser(userId, { isAdmin: !currentStatus });
            loadDashboardData();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to update user');
        }
    };

    const handleToggleActive = async (userId, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) return;
        try {
            await adminApi.updateUser(userId, { isActive: !currentStatus });
            loadDashboardData();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to update user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
        try {
            await adminApi.deleteUser(userId);
            loadDashboardData();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to delete user');
        }
    };

    if (loading && !stats) {
        return (
            <div className="adm-loading">
                <Loader2 size={32} className="adm-spin" />
            </div>
        );
    }

    return (
        <div className="adm-root">
            {/* Header */}
            <div className="adm-page-header">
                <div className="adm-page-header-inner">
                    <h1 className="adm-page-title">Admin Dashboard</h1>
                    <p className="adm-page-subtitle">Manage users and view platform statistics</p>
                </div>
            </div>

            <div className="adm-container">
                {/* Stats Grid */}
                <div className="adm-stats-grid">
                    <StatCard icon={<Users size={20} />} title="Total Users" value={stats?.users?.total || 0} accent="#2B8EE8" />
                    <StatCard icon={<UserCheck size={20} />} title="Active Users" value={stats?.users?.active || 0} accent="#22C55E" />
                    <StatCard icon={<Shield size={20} />} title="Admins" value={stats?.users?.admins || 0} accent="#8B5CF6" />
                    <StatCard icon={<TrendingUp size={20} />} title="New This Week" value={stats?.users?.newThisWeek || 0} accent="#F59E0B" />
                </div>

                {/* Users Table */}
                <div className="adm-card">
                    <div className="adm-card-header">
                        <h2 className="adm-card-title">Users</h2>
                        <div className="adm-search-wrap">
                            <Search size={15} className="adm-search-icon" />
                            <input
                                type="text"
                                placeholder="Search users…"
                                className="adm-search-input"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="adm-table-wrap">
                        <table className="adm-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Maps</th>
                                    <th>Limit</th>
                                    <th>Status</th>
                                    <th>Role</th>
                                    <th>Joined</th>
                                    <th className="adm-th-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => {
                                    const atLimit = (user.mapsCreated?.length || 0) >= (user.mapsLimit || 0);
                                    return (
                                        <tr key={user._id}>
                                            <td>
                                                <div className="adm-user-cell">
                                                    <img src={user.profilePicture} alt={user.name} className="adm-avatar" />
                                                    <Link to={`/admin/users/${user._id}`} className="adm-user-link">
                                                        {user.name}
                                                    </Link>
                                                </div>
                                            </td>
                                            <td className="adm-text-secondary">{user.email}</td>
                                            <td>{user.mapsCreated?.length || 0}</td>
                                            <td>
                                                <span className={`adm-badge ${atLimit ? 'adm-badge--red' : 'adm-badge--gray'}`}>
                                                    {user.mapsLimit || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`adm-badge ${user.isActive ? 'adm-badge--green' : 'adm-badge--red'}`}>
                                                    {user.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`adm-badge ${user.isAdmin ? 'adm-badge--purple' : 'adm-badge--gray'}`}>
                                                    {user.isAdmin ? 'Admin' : 'User'}
                                                </span>
                                            </td>
                                            <td className="adm-text-secondary">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="adm-td-right">
                                                <UserActionsMenu
                                                    user={user}
                                                    onToggleAdmin={handleToggleAdmin}
                                                    onToggleActive={handleToggleActive}
                                                    onDelete={handleDeleteUser}
                                                    onRefresh={loadDashboardData}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="adm-pagination">
                            <button
                                className="adm-btn adm-btn--ghost adm-btn--sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="adm-pagination-label">Page {currentPage} of {totalPages}</span>
                            <button
                                className="adm-btn adm-btn--ghost adm-btn--sm"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon, title, value, accent }) {
    return (
        <div className="adm-stat-card" style={{ '--accent': accent }}>
            <div className="adm-stat-icon">{icon}</div>
            <div className="adm-stat-body">
                <p className="adm-stat-label">{title}</p>
                <p className="adm-stat-value">{value}</p>
            </div>
        </div>
    );
}

function UserActionsMenu({ user, onToggleAdmin, onToggleActive, onDelete, onRefresh }) {
    const [isOpen, setIsOpen] = useState(false);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const buttonRef = React.useRef(null);

    const handleOpen = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.right - 200 });
        }
        setIsOpen(o => !o);
    };

    return (
        <>
            <div className="adm-menu-wrap">
                <button ref={buttonRef} className="adm-icon-btn" onClick={handleOpen}>
                    <MoreVertical size={16} />
                </button>

                {isOpen && (
                    <>
                        <div className="adm-menu-backdrop" onClick={() => setIsOpen(false)} />
                        <div className="adm-menu" style={{ top: menuPos.top, left: menuPos.left }}>
                            <button className="adm-menu-item" onClick={() => { setShowLimitModal(true); setIsOpen(false); }}>
                                <Map size={14} /> Set Maps Limit
                            </button>
                            <button className="adm-menu-item" onClick={() => { onToggleAdmin(user._id, user.isAdmin); setIsOpen(false); }}>
                                {user.isAdmin ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                                {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                            </button>
                            <button className="adm-menu-item" onClick={() => { onToggleActive(user._id, user.isActive); setIsOpen(false); }}>
                                {user.isActive ? <UserX size={14} /> : <UserCheckIcon size={14} />}
                                {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <div className="adm-menu-divider" />
                            <button className="adm-menu-item adm-menu-item--danger" onClick={() => { onDelete(user._id); setIsOpen(false); }}>
                                <Trash2 size={14} /> Delete User
                            </button>
                        </div>
                    </>
                )}
            </div>

            {showLimitModal && (
                <MapLimitModal
                    user={user}
                    onClose={() => setShowLimitModal(false)}
                    onUpdate={() => { setShowLimitModal(false); onRefresh(); }}
                />
            )}
        </>
    );
}

function MapLimitModal({ user, onClose, onUpdate }) {
    const [limit, setLimit] = useState(user.mapsLimit || 0);
    const [loading, setLoading] = useState(false);
    const atLimit = (user.mapsCreated?.length || 0) >= (user.mapsLimit || 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await adminApi.updateUser(user._id, { mapsLimit: parseInt(limit) });
            onUpdate();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to update limit');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="adm-overlay" onClick={onClose}>
            <div className="adm-modal" onClick={e => e.stopPropagation()}>
                <div className="adm-modal-header">
                    <h3 className="adm-modal-title">Set Maps Limit — {user.name}</h3>
                    <button className="adm-icon-btn" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="adm-modal-body">
                    <div className="adm-form-group">
                        <label className="adm-label">Maps Limit</label>
                        <input
                            type="number" min="0"
                            value={limit}
                            onChange={e => setLimit(e.target.value)}
                            className="adm-input"
                            required
                        />
                        <p className="adm-hint">Current usage: {user.mapsCreated?.length || 0} / {user.mapsLimit || 0} maps</p>
                        {atLimit && <p className="adm-hint adm-hint--red">User has reached their limit</p>}
                    </div>
                    <div className="adm-modal-footer">
                        <button type="button" className="adm-btn adm-btn--ghost" onClick={onClose} disabled={loading}>Cancel</button>
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={loading}>
                            {loading ? 'Updating…' : 'Update Limit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}